import { Injectable, inject } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/**
 * Sabe se existe uma página anterior DENTRO do app.
 *
 * Os botões "Voltar" das telas de detalhe navegavam para um destino fixo: a ficha do contato
 * sempre levava a Contatos, mesmo quando você tinha chegado nela a partir de Manutenções ou
 * Reservas. Voltar para um lugar onde você não estava é pior do que não ter botão.
 *
 * Usar `history.back()` sozinho não resolve: quem abre a ficha por link direto ou em aba nova
 * não tem página anterior, e o botão jogaria a pessoa para fora do sistema. Por isso contamos
 * as navegações desta sessão — só há "anterior" a partir da segunda.
 *
 * `history.length` não serve para isso: ele conta o histórico do navegador inteiro, inclusive o
 * que aconteceu antes de o app abrir.
 */
@Injectable({ providedIn: 'root' })
export class NavigationHistoryService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  private navegacoes = 0;

  constructor() {
    this.router.events
      .pipe(filter((evento) => evento instanceof NavigationEnd))
      .subscribe(() => this.navegacoes++);
  }

  /** Houve pelo menos uma tela antes desta, nesta sessão. */
  get temAnterior(): boolean {
    return this.navegacoes > 1;
  }

  /**
   * Volta para onde a pessoa estava. Sem página anterior, cai no destino informado — que é o
   * lugar onde aquele registro mora, e a melhor resposta quando não se sabe de onde veio.
   */
  voltar(destinoPadrao: unknown[]): void {
    if (this.temAnterior) {
      this.location.back();
      return;
    }
    this.router.navigate(destinoPadrao);
  }
}
